/**
 * ProseMirror commands implementing the screenplay element flow. All element
 * transitions come from flow.ts (data-driven from StyleDefs). Retyping a
 * paragraph to an allcaps style uppercases its stored text (Serbian locale),
 * matching Fade In, which stores text already uppercased.
 */
import { TextSelection, type Command, type Transaction } from "prosemirror-state";
import type { StyleDef } from "../format/types";
import { enterAction, tabAction, shiftTabAction } from "./flow";
import { screenplaySchema } from "./schema";

/** Serbian-latin uppercase (ć/č/đ/š/ž → Ć/Č/Đ/Š/Ž). Display uses CSS; this is
 * available for callers that need cased strings. */
export function upperSr(text: string): string {
  return text.toLocaleUpperCase("sr-Latn");
}

/**
 * Set a paragraph's style at `paraPos` (the position of the para node). allcaps
 * is a pure display concern (CSS text-transform), so stored text is never
 * mutated — this preserves round-trip fidelity (real files store mixed case).
 */
function retypeParaAt(
  tr: Transaction,
  paraPos: number,
  newStyle: string,
  _styles: Map<string, StyleDef>,
): void {
  const para = tr.doc.nodeAt(paraPos);
  if (!para || para.type.name !== "para") return;
  tr.setNodeMarkup(paraPos, undefined, { ...para.attrs, styleName: newStyle });
}

/** Retype every paragraph touched by the selection to `newStyle`. */
export function setElementType(
  styles: Map<string, StyleDef>,
  newStyle: string,
): Command {
  return (state, dispatch) => {
    const { from, to } = state.selection;
    const positions: number[] = [];
    state.doc.nodesBetween(from, to, (node, pos) => {
      if (node.type.name === "para") positions.push(pos);
      return false; // don't descend into paras
    });
    if (positions.length === 0) return false;
    if (!dispatch) return true;

    const tr = state.tr;
    // Descending order so earlier retypes don't shift later positions.
    for (const pos of positions.reverse()) {
      retypeParaAt(tr, pos, newStyle, styles);
    }
    dispatch(tr.scrollIntoView());
    return true;
  };
}

/** Info about the paragraph containing the caret. */
function currentPara(state: Parameters<Command>[0]) {
  const { $from } = state.selection;
  // Find the enclosing para (depth 1 in a flat schema).
  for (let d = $from.depth; d >= 0; d--) {
    const node = $from.node(d);
    if (node.type.name === "para") {
      return { node, pos: $from.before(d), depth: d };
    }
  }
  return null;
}

/** Enter: data-driven split / escape. */
export function elementEnter(styles: Map<string, StyleDef>): Command {
  return (state, dispatch) => {
    const info = currentPara(state);
    if (!info) return false;
    const current = String(info.node.attrs.styleName);
    const isEmpty = info.node.content.size === 0;
    const action = enterAction(styles, current, isEmpty);
    if (!dispatch) return true;

    const tr = state.tr;
    if (!state.selection.empty) tr.deleteSelection();

    if (action.kind === "retype") {
      retypeParaAt(tr, info.pos, action.style, styles);
    } else {
      const paraType = screenplaySchema.nodes.para;
      tr.split(tr.selection.from, 1, [
        { type: paraType, attrs: { styleName: action.style } },
      ]);
    }
    dispatch(tr.scrollIntoView());
    return true;
  };
}

/** Tab: data-driven cycle (empty) / new element (non-empty). */
export function elementTab(styles: Map<string, StyleDef>): Command {
  return (state, dispatch) => {
    const info = currentPara(state);
    if (!info) return false;
    const current = String(info.node.attrs.styleName);
    const isEmpty = info.node.content.size === 0;
    const action = tabAction(styles, current, isEmpty);
    if (!dispatch) return true;

    const tr = state.tr;
    if (action.kind === "retype") {
      retypeParaAt(tr, info.pos, action.style, styles);
    } else {
      const paraType = screenplaySchema.nodes.para;
      tr.split(tr.selection.from, 1, [
        { type: paraType, attrs: { styleName: action.style } },
      ]);
    }
    dispatch(tr.scrollIntoView());
    return true;
  };
}

/** Shift+Tab: cycle the current element backward. */
export function elementShiftTab(styles: Map<string, StyleDef>): Command {
  return (state, dispatch) => {
    const info = currentPara(state);
    if (!info) return false;
    const current = String(info.node.attrs.styleName);
    const action = shiftTabAction(styles, current);
    if (!dispatch) return true;
    const tr = state.tr;
    retypeParaAt(tr, info.pos, action.style, styles);
    dispatch(tr.scrollIntoView());
    return true;
  };
}

/** The style name of the paragraph containing the caret (for the status bar). */
export function currentStyleName(state: Parameters<Command>[0]): string | null {
  const info = currentPara(state);
  return info ? String(info.node.attrs.styleName) : null;
}

/** Place the caret inside the given paragraph index (used by UI after retype). */
export function selectParagraph(state: Parameters<Command>[0], index: number): Transaction {
  const tr = state.tr;
  let target = -1;
  state.doc.forEach((node, offset, i) => {
    if (i === index && node.type.name === "para") target = offset + 1;
  });
  if (target >= 0) {
    tr.setSelection(TextSelection.create(tr.doc, Math.min(target, tr.doc.content.size)));
  }
  return tr;
}
